import type { Paragraph } from '@/api/types/stowed-element'
import type { CandidAutoTaggingPayload, Tag, TagRow } from '@/api/types/photo'
import {
  DEFAULT_ALIGNMENT,
  DEFAULT_FONT_FAMILY_ID,
  DEFAULT_FONT_ID,
  DEFAULT_POINT_SIZE,
} from '@/lib/text/constants/text-defaults'
import type { CaptionFontStyle } from '@/lib/caption/types/caption-font-style'

const UNASSIGNED_ROW_NAME_LOWER = 'unassigned'

const FALLBACK_FONT_STYLE: CaptionFontStyle = {
  fontFamilyId: DEFAULT_FONT_FAMILY_ID,
  fontId: DEFAULT_FONT_ID,
  pointSize: DEFAULT_POINT_SIZE,
}

function makeParagraph(text: string, style: CaptionFontStyle): Paragraph {
  return {
    alignment: DEFAULT_ALIGNMENT,
    textLines: [
      {
        textRuns: [
          {
            textContent: text,
            fontId: style.fontId,
            fontFamilyId: style.fontFamilyId,
            pointSize: style.pointSize,
          },
        ],
      },
    ],
  }
}

// Guards against a tag whose firstName/lastName is missing (e.g. an "Unrecognized"
// face detection with no matched student) — without this, template interpolation
// would stringify `undefined` into the literal text "undefined undefined".
function formatName(tag: Tag): string {
  const firstName = tag.firstName ?? ''
  const lastName = tag.lastName ?? ''

  return `${firstName} ${lastName}`.trim()
}

function hasName(tag: Tag): boolean {
  return formatName(tag) !== ''
}

/**
 * True when at least one tag in the payload has an actual name to caption —
 * excludes payloads that only contain unnamed/unrecognized face detections.
 */
export function hasNamedCaptionTags(payload: CandidAutoTaggingPayload | undefined): boolean {
  const autoTaggingResponseList = payload?.autoTaggingResponseList ?? []

  return autoTaggingResponseList.some(hasName)
}

// A row with no real title (blank rowName) or the synthetic "Unassigned Row"
// bucket still has captionable names — it just doesn't get a "{rowName}:" label.
function isUnlabeledRow(rowName: string): boolean {
  const trimmed = rowName.trim()

  return trimmed === '' || trimmed.toLowerCase().startsWith(UNASSIGNED_ROW_NAME_LOWER)
}

function buildRowedTextLines(tags: Tag[], rows: TagRow[]): string[] {
  const lines: string[] = []

  const sortedRows = [...rows].sort((a, b) => {
    const ai = a.rowSortIndex ?? a.row
    const bi = b.rowSortIndex ?? b.row

    return ai - bi
  })

  for (const row of sortedRows) {
    const rowTagIds = new Set(row.idList ?? [])
    const rowTags = tags
      .filter((t) => rowTagIds.has(t.primaryId) && hasName(t))
      .sort((a, b) => a.tagIndex - b.tagIndex)

    if (rowTags.length === 0) continue

    const names = rowTags.map(formatName).join(', ')

    if (isUnlabeledRow(row.rowName)) {
      lines.push(names)
    } else {
      lines.push(`${row.rowName}:`, names)
    }
  }

  return lines
}

function buildFlatTextLines(tags: Tag[]): string[] {
  const sorted = tags.filter(hasName).sort((a, b) => {
    const ai = a.noRowIndex ?? a.tagIndex
    const bi = b.noRowIndex ?? b.tagIndex

    return ai - bi
  })

  if (sorted.length === 0) return []

  return [sorted.map(formatName).join(', ')]
}

/**
 * Builds the display text lines for candid auto-tagging data — shared by the
 * caption text box (`buildCaptionParagraphs`) and the Photo Info panel's
 * "Tagged:" field.
 *
 * When row view is enabled, returns names grouped by row in row-sort order:
 * a row with a real title gets a `"{rowName}:"` label line followed by its
 * names line; a row with no title or the synthetic "Unassigned Row" bucket
 * still contributes its names line, just without a label. Otherwise returns
 * a single line with all named tags in tag order. Tags with no matched name
 * (e.g. an "Unrecognized"/"Manual" face detection) are excluded. Returns an
 * empty array when there is nothing captionable.
 */
export function buildCaptionTextLines(payload: CandidAutoTaggingPayload | undefined): string[] {
  // Tag/row lists are typed as required arrays, but the backend can serialize an
  // empty/nil list as JSON `null` (common Go `encoding/json` behavior for a nil
  // slice) even when `payload` itself is present — normalize before touching `.length`.
  const autoTaggingResponseList = payload?.autoTaggingResponseList ?? []

  if (autoTaggingResponseList.length === 0) {
    return []
  }

  const rowResponseList = payload?.rowResponseList ?? []
  const rowViewEnable = payload?.rowViewEnable

  if (rowViewEnable && rowResponseList.length > 0) {
    return buildRowedTextLines(autoTaggingResponseList, rowResponseList)
  }

  return buildFlatTextLines(autoTaggingResponseList)
}

/**
 * Builds caption paragraphs from candid auto-tagging data (see
 * `buildCaptionTextLines`). Returns an empty single paragraph when there are
 * no tags.
 *
 * `style` defaults to the hardcoded system font constants; callers should pass
 * the resolved book/checkpoint default (see `resolveCaptionDefaultFont`) so
 * captions match the same default formatting as other new text boxes.
 */
export function buildCaptionParagraphs(
  payload: CandidAutoTaggingPayload | undefined,
  style: CaptionFontStyle = FALLBACK_FONT_STYLE
): Paragraph[] {
  const lines = buildCaptionTextLines(payload)

  if (lines.length === 0) {
    return [makeParagraph('', style)]
  }

  return lines.map((text) => makeParagraph(text, style))
}
